import { Fragment, useEffect, useState } from 'react';
import { editorActivityApi } from '../../../api/editorActivityApi';
import type { EditorActivitySummary } from '../../../types/editorActivity';
import { EditorActivityDetail } from './EditorActivityDetail';

function formatLastLogin(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Number of editors shown per page in the Admin Editor Activity list.
const PAGE_SIZE = 10;

// Feature 015 — Admin Editor Activity list (FR-017). One row per current editor;
// expanding a row reveals that editor's per-date detail. The list is paginated
// (10 editors per page) with a sticky header so column labels stay visible while
// scrolling a long page of editors.
export function EditorActivityTable() {
  const [editors, setEditors] = useState<EditorActivitySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    editorActivityApi
      .listActivity()
      .then(setEditors)
      .catch(() => setError('Could not load editor activity.'));
  }, []);

  if (error) {
    return <p className="text-red-600">{error}</p>;
  }

  if (editors === null) {
    return <p className="text-ink-soft">Loading…</p>;
  }

  if (editors.length === 0) {
    return <p className="text-ink-soft">No editors found.</p>;
  }

  const pageCount = Math.ceil(editors.length / PAGE_SIZE);
  const currentPage = Math.min(page, pageCount);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pageEditors = editors.slice(startIndex, startIndex + PAGE_SIZE);

  const goToPage = (next: number) => {
    setExpanded(null);
    setPage(next);
  };

  return (
    <div className="bg-card rounded-lg shadow border border-line">
      <div className="overflow-auto max-h-[70vh] rounded-t-lg">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-cream text-left text-ink-soft shadow-sm">
            <tr>
              <th className="py-2 px-4 font-medium">Editor</th>
              <th className="py-2 px-4 font-medium">Email</th>
              <th className="py-2 px-4 font-medium">Last login</th>
              <th className="py-2 px-4 font-medium">Active days</th>
              <th className="py-2 px-4 font-medium">Sessions</th>
              <th className="py-2 px-4 font-medium sr-only">Details</th>
            </tr>
          </thead>
          <tbody>
            {pageEditors.map((e) => {
            const isOpen = expanded === e.userId;
            return (
              <Fragment key={e.userId}>
                <tr className="border-t border-line hover:bg-cream">
                  <td className="py-2 px-4 text-ink">
                    {e.firstName} {e.lastName}
                  </td>
                  <td className="py-2 px-4 text-ink">{e.email}</td>
                  <td className="py-2 px-4 text-ink">{formatLastLogin(e.lastLoginAt)}</td>
                  <td className="py-2 px-4 text-ink">{e.activeDayCount}</td>
                  <td className="py-2 px-4 text-ink">{e.sessionCount}</td>
                  <td className="py-2 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : e.userId)}
                      aria-expanded={isOpen}
                      className="text-primary-700 hover:underline"
                    >
                      {isOpen ? 'Hide' : 'View'}
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-cream">
                    <td colSpan={6} className="py-3 px-4">
                      <EditorActivityDetail userId={e.userId} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
            })}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <nav
          className="flex items-center justify-between border-t border-line px-4 py-3"
          aria-label="Editor activity pagination"
        >
          <p className="text-sm text-ink-soft">
            Showing {startIndex + 1}–{startIndex + pageEditors.length} of {editors.length} editors
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="rounded border border-line-strong px-3 py-1 text-sm text-ink hover:bg-cream disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-ink-soft">
              Page {currentPage} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === pageCount}
              className="rounded border border-line-strong px-3 py-1 text-sm text-ink hover:bg-cream disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
